import { Alert, Box, ButtonGroup, Paper, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { Button } from '@verantos-dev/shared-components';

import { usePeopleAllocation } from './usePeopleAllocation';
import { AllocationConfiguration } from '../PeopleAllocation/AllocationConfiguration';
import { AnnotatorsSelection } from '../PeopleAllocation/AnnotatorsSelection';
import { SupervisorsSelection } from '../PeopleAllocation/SupervisorsSelection';

interface PeopleAllocationProps {
  setRequiredFieldsFilled: (fieldsFilled: boolean) => void;
}
export const PeopleAllocation = ({ setRequiredFieldsFilled }: PeopleAllocationProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { defaultAllocationMode, setAllocationModeDefault, setAllocationModeCustom } =
    usePeopleAllocation({ setRequiredFieldsFilled });
  return (
    <Paper
      sx={{
        p: theme.spacing(6),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(6),
      }}
    >
      <Typography variant="h6">
        {formatMessage({ id: 'createProjectWizard.step.allocation.title' })}
      </Typography>
      <AnnotatorsSelection />
      <SupervisorsSelection />

      <Box id="allocation-mode">
        <Typography variant="h6">
          {formatMessage({ id: 'createProjectWizard.step.allocation.allocationMode.title' })}
        </Typography>
        <ButtonGroup
          color="inherit"
          orientation="horizontal"
          variant="contained"
          sx={{ borderRadius: 0, m: theme.spacing(2), width: '100%' }}
        >
          <Button
            id="allocate-default-button"
            displayLabel={formatMessage({
              id: 'createProjectWizard.step.allocation.allocationMode.defaultAllocation',
            })}
            variant="contained"
            onClickHandler={setAllocationModeDefault}
            sx={{
              textTransform: 'none',
              flex: 1,
              fontSize: '14px',
              backgroundColor: defaultAllocationMode
                ? theme.palette.primary.main
                : theme.palette.grey[200],
              color: defaultAllocationMode ? theme.palette.white.main : theme.palette.text.primary,
              '&:hover': {
                backgroundColor: defaultAllocationMode
                  ? theme.palette.primary.dark
                  : theme.palette.grey[300],
              },
              '&:focus': {
                backgroundColor: defaultAllocationMode
                  ? theme.palette.primary.main
                  : theme.palette.grey[200],
              },
            }}
          />
          <Button
            id="allocate-custom-button"
            displayLabel={formatMessage({
              id: 'createProjectWizard.step.allocation.allocationMode.customAllocation',
            })}
            variant="contained"
            onClickHandler={setAllocationModeCustom}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              flex: 1,
              backgroundColor: !defaultAllocationMode
                ? theme.palette.primary.main
                : theme.palette.grey[200],
              color: !defaultAllocationMode ? theme.palette.white.main : theme.palette.text.primary,
              '&:hover': {
                backgroundColor: !defaultAllocationMode
                  ? theme.palette.primary.dark
                  : theme.palette.grey[300],
              },
              '&:focus': {
                backgroundColor: !defaultAllocationMode
                  ? theme.palette.primary.main
                  : theme.palette.grey[200],
              },
            }}
          />
        </ButtonGroup>
      </Box>
      {defaultAllocationMode && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {formatMessage({
            id: 'createProjectWizard.step.allocation.allocationConfiguration.defaultAllocationMode.description',
          })}
        </Alert>
      )}
      <AllocationConfiguration />
    </Paper>
  );
};
